import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import {
  entrySheetQueryOptions,
  requisitionQueryKeys,
  saveStudentCounts,
} from '../api/productRequisition.api';
import { RequisitionFilterPanel } from '../components/RequisitionFilterPanel';
import { StudentEntryGrid } from '../components/StudentEntryGrid';
import { useSheetDraft } from '../hooks/useSheetDraft';
import { cellKey, toEntryDraft, toQty, type EntryDraft } from '../utils/sheetDrafts';
import { useRequisitionFilters } from '../hooks/useRequisitionFilters';
import type { EntrySheetSearch } from '../types/productRequisition.types';
import { Can } from '@/components/common/Can';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';

const route = getRouteApi('/_auth/product/student-collection-projects/$projectId');
const LIST_ROUTE = '/product/student-collection-projects';

const NUMERIC_KEYS = ['instituteTypeId', 'thanaId'] as const;

export function StudentEntryPage() {
  const { projectId } = route.useParams();
  const queryClient = useQueryClient();
  const routeSearch = route.useSearch();
  const routeNavigate = route.useNavigate();
  const controller = useRequisitionFilters({
    search: routeSearch,
    numericKeys: NUMERIC_KEYS,
    onApplyFilters: (next) =>
      void routeNavigate({ search: (previous) => ({ ...previous, ...next }), replace: true }),
  });
  const params = controller.params as EntrySheetSearch;

  const { data, isLoading, error, refetch } = useQuery(entrySheetQueryOptions(projectId, params));

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [draft, setDraft] = useSheetDraft<typeof data, EntryDraft>(
    data,
    (sheet) => (sheet === undefined ? {} : toEntryDraft(sheet)),
    {},
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const counts = (data?.institutes ?? []).flatMap((institute) =>
        (data?.classCategories ?? []).flatMap((category) => {
          const key = cellKey(institute.instituteId, category.classId, category.classGroup);
          const value = draft[key];
          if (value === undefined || value.trim() === '') return [];
          return [
            {
              instituteId: institute.instituteId,
              classId: category.classId,
              classGroup: category.classGroup,
              studentQty: toQty(value),
            },
          ];
        }),
      );

      if (counts.length === 0) throw new Error('Nothing to save');
      return saveStudentCounts(projectId, counts);
    },
    onSuccess: (saved) => {
      toast.success(`Saved ${saved.stored} cell(s), ${saved.studentQty} students`);
      void queryClient.invalidateQueries({ queryKey: requisitionQueryKeys.all() });
    },
    onError: () => toast.error('Could not save the sheet'),
  });

  const draftTotal = useMemo(
    () => Object.values(draft).reduce((sum, value) => sum + toQty(value), 0),
    [draft],
  );

  const stats = [
    { key: 'institutes', label: 'Institutes', value: data?.institutes.length },
    { key: 'classes', label: 'Class Rows', value: data?.classCategories.length },
    { key: 'students', label: 'Students', value: draftTotal },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={data?.projectName ?? 'Student Entry'}
        leading={<BackButton onClick={() => void routeNavigate({ to: LIST_ROUTE })} />}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <Can code={PRODUCT_PERMISSIONS.STUDENT_PROJECT_EDIT}>
              <Button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || data === undefined || !(data?.isOpen ?? false)}
              >
                Save Sheet
              </Button>
            </Can>
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <RequisitionFilterPanel filters={controller} withGeo withInstituteType />
      </FilterPanelCollapse>

      {params.instituteTypeId === undefined ? (
        <EmptyState
          title="Pick an institute type"
          description="The class rows a sheet shows depend on the institute type, so choose one to open it."
        />
      ) : (
        <>
          <StatCardGrid stats={stats} isLoading={isLoading} isError={error !== null} />

          {error ? (
            <ErrorState
              error={error}
              fallbackTitle="Could not load the entry sheet"
              onRetry={() => void refetch()}
              fill
            />
          ) : isLoading ? (
            <ListSkeleton />
          ) : data === undefined || data.institutes.length === 0 ? (
            <EmptyState
              title="No institute to sheet"
              description="No institute is assigned to you under this type and thana."
            />
          ) : (
            <StudentEntryGrid
              sheet={data}
              draft={draft}
              onChange={(key, value) => setDraft((previous) => ({ ...previous, [key]: value }))}
            />
          )}
        </>
      )}
    </div>
  );
}
